<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_GL_HISTORY_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="accountNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <template #cell(transactionDate)="data">
        <p class="mb-0">
          {{ data.value ? formatDateTime(data.value) : '' }}
        </p>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      no-close-on-backdrop
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="glHistoryRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="gl-code"
                v-model="filter.glCode"
                :label="$t('GL Code')"
                :placeholder="$t('Enter GL Code')"
                type="text"
              />
            </b-col>
            <!-- Account Number -->
            <!-- Posting Reference Number -->
            <b-col md="6">
              <InputField
                id="posting-reference-number"
                v-model="filter.postingReferenceNumber"
                :label="$t('Posting Reference Number')"
                :placeholder="$t('Enter Reference Number')"
                type="text"
                for="posting-reference-number"
              />
            </b-col>
            <!-- Posting Reference Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :label="$t('Branch')"
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <!-- Instrument Number -->
            <b-col md="6">
              <InputField
                id="instrument-number"
                v-model="filter.instrumentNumber"
                :label="$t('Instrument Number')"
                :placeholder="$t('Enter Instrument Number')"
                type="text"
                for="instrument-number"
              />
            </b-col>
            <!-- Instrument Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                :error-name="$t('Financial Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                :min-date="new Date()"
                :label="$t('Transaction Date (To)')"
                :error-name="$t('Transaction Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
            <!-- End Date -->
          </b-row>

          <b-row class="match-height">
            <!-- Sort Code -->
            <b-col md="6">
              <InputField
                id="entry-code"
                v-model="filter.entryCode"
                :label="$t('Sort Code')"
                :placeholder="$t('Enter Sort Code')"
                type="text"
                for="entry-code"
              />
            </b-col>
            <!-- Sort Code -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BForm, BSpinner,
  BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { formatAmount, toBalance } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import Button from '../../../components/button/Button.vue'
import toNumber from '../../../../helpers/toNumber'
import { toCustomDateTime, toCustomDate } from '../../../../helpers/formatDate'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    BForm,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    ValidationObserver,
    DatePickerField,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Customer Account History Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'glCode', sortable: true, label: 'GL Code' },
        { key: 'glName', sortable: true, label: 'GL Name' },
        { key: 'postedBy', sortable: true, label: 'Posted By' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Reference Number' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'branch', sortable: true, label: 'Branch Name' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'instrumentNumber', sortable: true, label: 'Narration' },
      ],
      filter: {
        glCode: '',
        postingReferenceNumber: '',
        financialDateFrom: null,
        financialDateTo: null,
        instrumentNumber: '',
        entryCode: '',
        branchCode: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,

    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Financial Date': this.formatCustomDate(item.financialDate),
        'Transaction Date': this.formatDateTime(item.transactionDate),
        'GL Code': item.glCode,
        'GL Name': item.glName,
        'Posted By': item.postedBy,
        'Posting Ref. No.': item.postingReferenceNumber,
        Debit: item.debit ? this.toAmount(item.debit) : 0,
        Credit: item.credit ? this.toAmount(item.credit) : 0,
        Narration: item.narration,
        'Sort Code': item.entryCode,
        'Instrument Number': item.instrumentNumber,
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchGLHistoryReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchGLHistoryReport()
    },
  },
  mounted() {
    this.fetchGLHistoryReport()
  },
  methods: {
    ...mapActions({
      getGLHistories: 'reports/getGLHistories',
      getBranchByCode: 'operations/getBranchByCode',
      downloadGLHistoryReport: 'reports/downloadGLHistoryReport',
    }),

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchGLHistoryReport()
    },

    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },

    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.glHistoryRules.validate().then(async success => {
        if (success) {
          this.fetchGLHistoryReport()
        }
      })
    },
    async fetchGLHistoryReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getGLHistories({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data.map(item => ({
            ...item,
            debit: toBalance(item.debit),
            credit: toBalance(item.credit),
          }))
          this.totalCount = data.recordCount
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadGLHistoryReport({
          search: this.filter,
          fileExportFormat: 1,
          permission: reportPermissions.EXPORT_GL_HISTORY_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadGLHistoryReport({
          search: this.filter,
          fileExportFormat: 0,
          permission: reportPermissions.EXPORT_GL_HISTORY_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    toAmount: formatAmount,
    formatNumber: toNumber,
    formatDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
import { mapGetters } from 'vuex/types/helpers'
